/**
 * @description Centralizes flow endpoint resolving for canvas clicks, validation, persistence, and rendering.
 */

import { Flow, ManualArea, RackingArea, SingularStationArea } from '@thive/fddm-types';
import { Vector3 } from 'three';

import { logger } from '@common/logging/logger';
import {
  areasToFlatCollection,
  isRackingArea,
  isSingularStationArea,
} from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import {
  getAreaLoadHandlingType,
  isLoadHandlingArea,
} from '@/modules/floorplanDesign/helpers/areaLoadHandling';
import {
  isManualArea,
  isShuttleRackingArea,
  isShuttleStorageRackingArea,
} from '@/modules/floorplanDesign/helpers/typeguards';
import { Area, AreaLoadHandlingType } from '@/modules/floorplanDesign/types';
import { FlowStopType } from '@/modules/flowsV2/store/useActiveFlowStore';

import { getAreaAttachPoint, getEndpointBaseId, getGroupAttachPoint } from './helpers';
import { assertNever } from '@/helpers/utils';

export type FlowLoadHandlingType =
  | SingularStationArea['loadHandlingType']
  | ManualArea['loadHandlingType']
  | RackingArea['rackingLoadHandlingType'];

export type FlowData = {
  selectedFlowStopOrAreaId: string;
  flowStopType: FlowStopType;
  loadHandlingType: AreaLoadHandlingType | undefined;
};

const getLoadHandlingTypeFromArea = (
  area: Area | undefined,
  errorMessage: string,
): AreaLoadHandlingType | undefined => {
  if (!area) return undefined;

  if (!isLoadHandlingArea(area)) {
    logger.error(errorMessage, area);
    return undefined;
  }

  return getAreaLoadHandlingType(area);
};

export const getFlatFlowStopAreas = (flowStopId: string): Area[] | undefined => {
  const { getFlowStopById, getAreasByIds } = useFloorplanDesignStore.getState();
  const flowStop = getFlowStopById(flowStopId);

  if (!flowStop) {
    logger.error(`Functional group with id ${flowStopId} not found`);
    return undefined;
  }

  return areasToFlatCollection(getAreasByIds(flowStop.areaIds));
};

export const getFirstFlowStopArea = (flowStopId: string): Area | undefined => {
  const [area] = getFlatFlowStopAreas(flowStopId) ?? [];

  if (!area) {
    logger.error(`Group with id ${flowStopId} has no areas`);
    return undefined;
  }

  return area;
};

export const getShuttleStorageAreaByBaseId = (baseId: string): Area | undefined => {
  const { getAreaById, getAreasByIds, getFlowStopById } = useFloorplanDesignStore.getState();

  const directStorageArea = getAreaById(`${baseId}/Storage`);

  if (directStorageArea && isShuttleStorageRackingArea(directStorageArea)) {
    return directStorageArea;
  }

  const baseArea = getAreaById(baseId);

  if (baseArea && isShuttleStorageRackingArea(baseArea)) {
    return baseArea;
  }

  const baseFlowStop = getFlowStopById(baseId);

  if (!baseFlowStop) return undefined;

  const flowStopAreas = areasToFlatCollection(getAreasByIds(baseFlowStop.areaIds));

  return flowStopAreas.find((flowStopArea) => isShuttleStorageRackingArea(flowStopArea));
};

/**
 * Returns the area id used for validation rules.
 * Used so shuttle sub-endpoints are validated against their storage area.
 */
export const getFlowRuleAreaId = (id: string): string => {
  const baseId = getEndpointBaseId(id);
  const shuttleStorageArea = getShuttleStorageAreaByBaseId(baseId);

  if (shuttleStorageArea) return shuttleStorageArea.id;

  return baseId;
};

/**
 * Returns the endpoint id that should be saved on the flow.
 * Used to avoid saving child ids like "SH01/Storage" when the flow should belong to "SH01".
 */
export const getPersistedFlowEndpointId = (id: string): string => {
  const baseId = getEndpointBaseId(id);
  const shuttleStorageArea = getShuttleStorageAreaByBaseId(baseId);

  if (shuttleStorageArea) return shuttleStorageArea.id;

  return id;
};

export const getRenderableFlowEndpointId = (id: string): string => {
  const { getAreaById, getFlowStopById } = useFloorplanDesignStore.getState();

  if (getAreaById(id)) return id;
  if (getFlowStopById(id)) return id;

  const baseId = getEndpointBaseId(id);

  if (baseId === id) return id;
  if (getAreaById(baseId)) return baseId;
  if (getFlowStopById(baseId)) return baseId;

  return id;
};

export const isShuttleFlowEndpoint = (flowStopType: FlowStopType, id: string): boolean => {
  const { getAreaById } = useFloorplanDesignStore.getState();

  const isShuttleStorageLikeArea = (area: Area | undefined): boolean =>
    Boolean(
      area &&
        isRackingArea(area) &&
        area.rackingLoadHandlingType === 'STORAGE' &&
        area.id.endsWith('/Storage'),
    );

  if (flowStopType === 'area') {
    const area = getAreaById(id);

    if ((area && isShuttleRackingArea(area)) || isShuttleStorageLikeArea(area)) return true;

    const baseId = getEndpointBaseId(id);

    if (baseId === id) return false;

    const baseArea = getAreaById(baseId);

    if (isShuttleStorageLikeArea(getAreaById(`${baseId}/Storage`))) return true;

    return Boolean(baseArea && isShuttleRackingArea(baseArea));
  }

  const flowStopAreas = getFlatFlowStopAreas(id);

  if (!flowStopAreas) return false;

  return flowStopAreas.some(
    (area) => isShuttleRackingArea(area) || isShuttleStorageLikeArea(area),
  );
};

export const getFlowStopLoadHandlingType = (
  id: string,
  flowStopType: FlowStopType,
): FlowLoadHandlingType | undefined => {
  const {
    getAreaById,
    getFlowStopById,
    getFlowStopLoadHandlingType: getFlowStopLoadHandlingTypeFromStore,
  } = useFloorplanDesignStore.getState();

  if (flowStopType === 'flowStop') {
    const flowStop = getFlowStopById(id);

    if (!flowStop) {
      logger.error(`Functional group with id ${id} not found`);
      return undefined;
    }

    return getFlowStopLoadHandlingTypeFromStore(id);
  }

  const area = getAreaById(id);

  if (!area) {
    logger.error(`Area with id ${id} not found`);
    return undefined;
  }

  if (isSingularStationArea(area) || isManualArea(area)) return area.loadHandlingType;
  if (isRackingArea(area)) return area.rackingLoadHandlingType;

  logger.error('Invalid area type for load handling type:', area);
  return undefined;
};

export const getFlowStopAttachPoint = (
  id: string,
  flowStopType: FlowStopType,
): Vector3 | undefined => {
  const { getAreaById } = useFloorplanDesignStore.getState();

  if (flowStopType === 'flowStop') {
    const flowStopAreas = getFlatFlowStopAreas(id);

    if (!flowStopAreas) return undefined;

    const point = getGroupAttachPoint(flowStopAreas);

    return new Vector3(point.x, point.y, 0);
  }

  const area = getAreaById(id);

  if (!area) {
    logger.error(`Area with id ${id} not found`);
    return undefined;
  }

  if (!isSingularStationArea(area) && !isRackingArea(area) && !isManualArea(area)) {
    logger.error('Invalid area type for flow stop:', area);
    return undefined;
  }

  const point = getAreaAttachPoint(area.rectangle);

  return new Vector3(point.x, point.y, 0);
};

const getFlowStopFlowData = (flowStopId: string): FlowData | undefined => {
  const { getFlowStopById, getAreasByIds } = useFloorplanDesignStore.getState();
  const flowStop = getFlowStopById(flowStopId);

  if (!flowStop) return undefined;

  const flowStopAreas = areasToFlatCollection(getAreasByIds(flowStop.areaIds));
  const shuttleStorageArea = flowStopAreas.find((area) => isShuttleStorageRackingArea(area));

  if (shuttleStorageArea) {
    return {
      selectedFlowStopOrAreaId: shuttleStorageArea.id,
      flowStopType: 'area',
      loadHandlingType: getLoadHandlingTypeFromArea(
        shuttleStorageArea,
        'Shuttle flow stop has no storage load handling area:',
      ),
    };
  }

  const [referenceArea] = flowStopAreas;

  return {
    selectedFlowStopOrAreaId: flowStop.id,
    flowStopType: 'flowStop',
    loadHandlingType: getLoadHandlingTypeFromArea(
      referenceArea,
      'Flow stop has no load handling area:',
    ),
  };
};

const getShuttleStorageFlowData = (baseElementId: string): FlowData | undefined => {
  const shuttleStorageArea = getShuttleStorageAreaByBaseId(baseElementId);

  if (!shuttleStorageArea) return undefined;

  return {
    selectedFlowStopOrAreaId: shuttleStorageArea.id,
    flowStopType: 'area',
    loadHandlingType: getLoadHandlingTypeFromArea(
      shuttleStorageArea,
      'Shuttle area has no storage load handling type:',
    ),
  };
};

const getAreaFromElementId = (elementId: string, baseElementId: string): Area | undefined => {
  const { getAreaById } = useFloorplanDesignStore.getState();
  const directArea = getAreaById(elementId);

  if (directArea) return directArea;

  if (baseElementId === elementId) return undefined;

  return getAreaById(baseElementId);
};

const getFlowDataFromRelatedFlowStop = (area: Area): FlowData | undefined => {
  const { getRelatedFlowStop, getAreasByIds } = useFloorplanDesignStore.getState();
  const relatedFlowStop = getRelatedFlowStop(area.id);

  if (!relatedFlowStop) return undefined;

  const relatedFlowStopData = getFlowStopFlowData(relatedFlowStop.id);

  if (relatedFlowStopData) return relatedFlowStopData;

  const [referenceArea = area] = areasToFlatCollection(getAreasByIds(relatedFlowStop.areaIds));

  return {
    selectedFlowStopOrAreaId: relatedFlowStop.id,
    flowStopType: 'flowStop',
    loadHandlingType: getLoadHandlingTypeFromArea(
      referenceArea,
      'Flow stop has no load handling area:',
    ),
  };
};

export const getFlowDataFromElement = (elementId: string): FlowData | undefined => {
  const directFlowStopData = getFlowStopFlowData(elementId);

  if (directFlowStopData) return directFlowStopData;

  const baseElementId = getEndpointBaseId(elementId);

  if (baseElementId !== elementId) {
    const baseFlowStopData = getFlowStopFlowData(baseElementId);

    if (baseFlowStopData) return baseFlowStopData;
  }

  const area = getAreaFromElementId(elementId, baseElementId);

  if (!area) {
    // Could be a shuttle template group (GroupLogical) on the canvas — try resolving via /Storage sub-area
    const shuttleStorageFlowData = getShuttleStorageFlowData(baseElementId);
    if (shuttleStorageFlowData) return shuttleStorageFlowData;

    logger.error('Could not find area or flow stop for element id:', elementId);
    return undefined;
  }

  if (isShuttleRackingArea(area)) {
    const shuttleStorageFlowData = getShuttleStorageFlowData(baseElementId);

    if (shuttleStorageFlowData) return shuttleStorageFlowData;
  }

  const relatedFlowStopData = getFlowDataFromRelatedFlowStop(area);

  if (relatedFlowStopData) return relatedFlowStopData;

  return {
    selectedFlowStopOrAreaId: area.id,
    flowStopType: 'area',
    loadHandlingType: getLoadHandlingTypeFromArea(area, 'Area has no load handling type:'),
  };
};

export const isFlowTypeAllowedForEndpoint = (
  flowType: Flow['type'],
  flowStopType: FlowStopType,
  id: string,
): boolean => {
  const isShuttleEndpoint = isShuttleFlowEndpoint(flowStopType, id);

  if (!isShuttleEndpoint) return true;

  switch (flowType) {
    case 'CONSOLIDATION_STORAGE':
    case 'CONSOLIDATION_RETRIEVAL':
      return true;
    case 'TRANSPORT':
    case 'EXTERNAL':
    case 'GOODS_TO_PERSONS':
      return false;
    default:
      assertNever(flowType);
  }
};
